/**
 * Helpers for capturing WHAT a student actually said when a letter/sound
 * response is marked incorrect (v3.27.0).
 *
 * During a letter-knowledge assessment the teacher can press a letter key
 * instead of ← : the item is marked Incorrect AND the pressed letter is
 * recorded as the student's response. Stored on the assessment doc as a
 * top-level `incorrectResponses` map ({ [assessedLetter]: heardLetter }),
 * parallel to `responses` / `letterFluency` so no existing consumer of
 * `responses` changes shape. Only letter_knowledge tests use this.
 */

/**
 * True when a keydown event is a plain single-letter key press (no
 * Ctrl/Meta/Alt chords, so browser shortcuts like Cmd+R keep working).
 */
export function isPlainLetterKeyEvent(e) {
  return /^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey;
}

/**
 * Which tests show LOWERCASE glyphs on the card: the lowercase test, the two
 * optional vowel tests (v3.73.0 — long_vowels / short_vowels show the
 * lowercase vowel), and — v3.85.0 — a Sounds run given in lowercase (the
 * run's `letterCase`; absent = uppercase, every doc before v3.85.0).
 * Uppercase, plain Sounds and a both-case ("M m") Sounds run keep the
 * uppercase (canonical) shapes. This is the one case rule both helpers below
 * share; the three letter tests' branches are unchanged.
 */
const showsLowercaseGlyphs = (testType, letterCase) =>
  testType === 'lowercase' || testType === 'long_vowels' || testType === 'short_vowels'
  || (testType === 'sounds' && letterCase === 'lowercase');

/**
 * Normalize the pressed key to the case convention of the test type, so the
 * recorded response reads like the letters the student sees: lowercase for
 * the lowercase test (and the vowel tests, and a lowercase Sounds run),
 * uppercase for uppercase and sounds (sounds reuses the uppercase letter
 * shapes).
 */
export function normalizeHeardLetter(key, testType, letterCase) {
  if (typeof key !== 'string' || key.length !== 1) return null;
  return showsLowercaseGlyphs(testType, letterCase) ? key.toLowerCase() : key.toUpperCase();
}

/**
 * Classify a letter-key press against the letter being assessed (v3.29.0).
 *
 * The teacher types WHAT THE STUDENT SAID — so if the pressed letter matches
 * the displayed letter, the student answered correctly and the press counts
 * as Correct (no heard letter recorded). Any other letter counts as
 * Incorrect with that letter recorded. Returns null when inputs are unusable
 * (caller should ignore the key press). `letterCase` (v3.85.0) is the run's
 * case for a Sounds variant; omitted = uppercase, as before.
 */
export function classifyLetterKeyResponse(key, currentLetter, testType, letterCase) {
  const heard = normalizeHeardLetter(key, testType, letterCase);
  if (!heard || typeof currentLetter !== 'string' || currentLetter.length !== 1) return null;
  const shown = showsLowercaseGlyphs(testType, letterCase) ? currentLetter.toLowerCase() : currentLetter.toUpperCase();
  return heard === shown ? { correct: true, heard: null } : { correct: false, heard };
}

/**
 * Build the tooltip for a Results detailed-grid cell.
 * - Correct cells: response time only (unchanged information, now visible).
 * - Incorrect cells: the student's captured response (when one was recorded)
 *   plus response time.
 * Returns undefined when there is nothing to show (e.g. manual entries and
 * pre-v3.27.0 assessments with no fluency data).
 */
export function buildResponseCellTooltip(finalResponse, heardLetter, fluencyMs) {
  const parts = [];
  if (finalResponse === 'Incorrect' && heardLetter) {
    parts.push(`Student said: ${heardLetter}`);
  }
  if (typeof fluencyMs === 'number' && fluencyMs >= 0) {
    parts.push(`Response time: ${(fluencyMs / 1000).toFixed(1)}s`);
  }
  return parts.length > 0 ? parts.join(' — ') : undefined;
}
